import React, { useState, useEffect, useLayoutEffect } from "react";
import Modal from "components/UI/Modal/Modal";
import { InsertModal, UpdateModal, DeleteModal } from "./Actions";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { routeInfo, userCredentials } from "recoilState/GlobalState";
import { HeaderButton } from "components";
import Add from "@mui/icons-material/Add";
import Cached from "@mui/icons-material/Cached";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FilterAltOffRoundedIcon from '@mui/icons-material/FilterAltOffRounded';
import { Buttons } from "components";
import { ACTIONS } from "consts/Actions";
import { ServicioTitles as titles } from "consts/titulos-de-tablas";
import styles from "styles/pages/Pages.module.scss";
import filtroStyles from "./index.module.scss";
import { useService, puedeEditarServicio } from "components/route/ServiceRoute";
import XGrid from "components/UI/Grid/XGrid";
import { RNHUnidadGet } from "services/services-mia/programa-salud/estructuras/RNHUnidadGet";
import { etiquetaSiNo } from "consts/banderas";
import Filters from "components/Filters";
import { useFormik } from "formik";
import { RNHDepartamentoGet } from "services/services-mia/programa-salud/estructuras/RNHDepartamentoGet";
import { RNHDivisionGet } from "services/services-mia/programa-salud/estructuras/RNHDivisionGet";

export default function Unidades() {
  const credentials = useRecoilValue(userCredentials);
  const setRoute = useSetRecoilState(routeInfo);
  const [modalDetails, setModalDetails] = useState({ isOpen: false });
  const [loading, setLoading] = useState(false);
  const [unidades, setUnidades] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [divisiones, setDivisiones] = useState([]);
  const [loadingDptos, setLoadingDptos] = useState(false);
  const [loadingDivisiones, setLoadingDivisiones] = useState(false);
  const [rowSelected, setRowSelected] = useState({ status: false, row: {} });

  const [filtroDpto, setFiltroDpto] = useState({ departamentoId: "", divisionId: "" });
  const { service } = useService();

  const getUnidades = async (filtro = filtroDpto) => {
    setUnidades([]);
    setRowSelected({ status: false, row: {} });
    setLoading(true);
    const { data: result } = await RNHUnidadGet(credentials, {
      departamentoId: filtro.departamentoId || null,
      divisionId: filtro.divisionId || null,
    });
    const newRows = result[0]?.rnhUnidadId ? result?.map((data) => {
      return {
        id: data.rnhUnidadId,
        cod: data.rnhUnidadCod,
        name: data.rnhUnidadNombre,
        desc: data.rnhUnidadDescripcion,
        // `||` y no `??`: el back manda tanto null como "" cuando no hay, y la
        // celda vacia se lee como "falta el dato" en vez de "no tiene".
        dpto: data.rnhDepartamentoNombre || "Sin asignar",
        dptoId: data.rnhDepartamentoId,
        division: data.rnhDivisionNombre || "Sin asignar",
        divisionId: data.rnhDivisionId,

        // Los dos permisos, en sus dos formas: la ETIQUETA para la celda y el CRUDO
        // "Y"/"N" que precarga el modal de edicion. Mismo criterio que `status` y
        // `statusCod` en el detalle del agente.
        rotativos: etiquetaSiNo(data.rnhUnidadAdmiteRotativos),
        guardias: etiquetaSiNo(data.rnhUnidadAdmiteGuardias),
        admiteRotativos: data.rnhUnidadAdmiteRotativos,
        admiteGuardias: data.rnhUnidadAdmiteGuardias,

        // No son columnas: viajan en la fila para que Modificar y Eliminar no
        // tengan que volver a pedir el registro.
        version: data.rnhUnidadVersion,
      };
    }) : []
    setUnidades(newRows);
    setLoading(false);
    return {
      rows: newRows,
      rowCount: newRows.length,
    };
  }
  const closeModal = () => {
    setModalDetails({ isOpen: false });
  };
  const handleClick = ({ procede, title }) => {
    setModalDetails({ procede, title, isOpen: true });
  };

  const filtros = useFormik({
    initialValues: { departamentoId: "", divisionId: "" },
    onSubmit: (values) => {
      const filtro = { departamentoId: values.departamentoId, divisionId: values.divisionId };
      setFiltroDpto(filtro);
      getUnidades(filtro); // el filtrado de unidades va por REQUEST
    },
  });

  const limpiarFiltros = () => {
    filtros.setFieldValue("departamentoId", "");
    filtros.setFieldValue("divisionId", "");
    const vacio = { departamentoId: "", divisionId: "" };
    setFiltroDpto(vacio);
    getUnidades(vacio);
  };

  const getDepartamentos = async () => {
    setLoadingDptos(true);
    const { data: result } = await RNHDepartamentoGet(credentials);
    const rows = result[0]?.rnhDepartamentoId
      ? result.map((d) => ({
        id: d.rnhDepartamentoId,
        cod: d.rnhDepartamentoCod,
        name: d.rnhDepartamentoNombre,
        desc: d.rnhDepartamentoDescripcion,
      }))
      : [];
    setDepartamentos(rows);
    setLoadingDptos(false);
  }

  const getDivision = async () => {
    setDivisiones([]);
    setLoadingDivisiones(true);
    const { data: result } = await RNHDivisionGet(credentials);
    const newRows = result[0]?.rnhDivisionId ? result?.map((data) => {
      return {
        id: data.rnhDivisionId,
        cod: data.rnhDivisionCod,
        name: data.rnhDivisionNombre,
        desc: data.rnhDivisionDescripcion,
        // `||` y no `??`: el back manda tanto null como "" cuando no hay, y la
        // celda vacia se lee como "falta el dato" en vez de "no tiene".
        dpto: data.rnhDepartamentoNombre || "Sin asignar",
        dptoId: data.rnhDepartamentoId
      };

    }) : []
    setDivisiones(newRows);
    setLoadingDivisiones(false);
  }


  useEffect(() => {
    getUnidades()
    getDepartamentos()
    getDivision()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useLayoutEffect(() => {
    setRoute({
      wordKey: `Unidades - Programa Salud RN`
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.servicioNombre, setRoute])

  // Al cambiar el Departamento (en el form) se resetea la División elegida, para no
  // dejar una división que no pertenezca al depto (evita combinaciones inválidas).
  useEffect(() => {
    filtros.setFieldValue("divisionId", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.values.departamentoId])

  // Opciones de División filtradas CLIENT-SIDE por el depto elegido en el form:
  // depto "Todos" (vacío) → todas las divisiones; un depto → solo las suyas. Así no se
  // puede elegir depto+división que no concuerdan. El filtrado de UNIDADES va por request.
  const divisionesDelDepto = filtros.values.departamentoId
    ? divisiones.filter((d) => String(d.dptoId) === String(filtros.values.departamentoId))
    : divisiones;

  return (
    <main className={styles.main}>
      <Modal size={modalDetails.procede === ACTIONS.DELETE ? 'sm' : 'md'} open={modalDetails.isOpen} title={modalDetails.title} onClose={closeModal}>
        {modalDetails.procede === ACTIONS.UPDATE && (
          <UpdateModal
            row={rowSelected.row}
            updateList={getUnidades}
            closeModal={closeModal}
            departamentos={departamentos}
            loadingDptos={loadingDptos}
            divisiones={divisiones}
            loadingDivisiones={loadingDivisiones}
          />
        )}
        {modalDetails.procede === ACTIONS.DELETE && (
          <DeleteModal
            row={rowSelected.row}
            servicio={service}
            updateList={getUnidades}
            closeModal={closeModal}
            setterRowSelected={setRowSelected}
          />
        )}
        {modalDetails.procede === ACTIONS.INSERT && (
          <InsertModal
            servicio={service}
            updateList={getUnidades}
            closeModal={closeModal}
            departamentos={departamentos}
            loadingDptos={loadingDptos}
            divisiones={divisiones}
            loadingDivisiones={loadingDivisiones}
          />
        )}
      </Modal>
      <div className={styles["filters-actions"]}>
        <div className={filtroStyles.filtroAncho} style={{ display: "flex", alignItems: "center" }}>
          <Filters
            formik={filtros}
            inputsRender={[
              {
                type: "select",
                name: "departamentoId",
                label: "Departamento",
                disabled: loadingDptos,
                options: departamentos,
                firstOptionKey: "id",
                firstOptionValue: "id",
                firstOptionName: "name",
                all: true,
              },
              {
                type: "select",
                name: "divisionId",
                label: "División",
                disabled: loadingDivisiones,
                options: divisionesDelDepto,
                firstOptionKey: "id",
                firstOptionValue: "id",
                firstOptionName: "name",
                all: true,
              },
            ]}
            disabled={loading || loadingDptos}
          />

          <HeaderButton text={"Borrar filtros"} onClick={limpiarFiltros} disabled={loading || loadingDptos}>
            <FilterAltOffRoundedIcon />
          </HeaderButton>
        </div>
        <Buttons>
          <HeaderButton
            text={"Actualizar"}
            onClick={() => { getUnidades() }}
            disabled={rowSelected.row.id || false}
          >
            <Cached />
          </HeaderButton>
          <HeaderButton
            text={"Nueva Unidad"}
            handleClick={handleClick}
            data={{
              title: "Nueva Unidad",
              procede: ACTIONS.INSERT
            }}
            disabled={rowSelected.row.id || service.servicioPermiso !== 'E'}
          >
            <Add />
          </HeaderButton>
          <HeaderButton
            text={"Modificar Unidad"}
            handleClick={handleClick}
            data={{
              title: "Modificar Unidad",
              procede: ACTIONS.UPDATE
            }}
            disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
          >
            <EditOutlined />
          </HeaderButton>
          <HeaderButton
            text={"Eliminar Unidad"}
            handleClick={handleClick}
            data={{
              title: "Eliminar Unidad",
              procede: ACTIONS.DELETE
            }}
            disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
          >
            <DeleteOutlineRoundedIcon />
          </HeaderButton>
        </Buttons>
      </div>
      <div className={styles.container}>
        <XGrid
          rows={unidades}
          titles={titles}
          loading={loading}
          secondarySelected={rowSelected}
          setSecondarySelected={setRowSelected}
        />
      </div>
    </main>
  );
}